import React, { FC, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import cn from "clsx";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import { useTranslation } from "react-i18next";
import { getOptionalByValue } from "../../../util";
import {
  selectCommonSettings,
  selectLanguageSlug,
  selectMessagesFromConfigs
} from "../../../util/selectors";
import { selectPDPPriceInfo, selectProductDetails } from "../selectors";
import useBreakpoint from "../../../hooks/useBreakpoint";
import { useCurrency, useIsRTL } from "../../../hooks";
import ModalBfl from "../../../components/modal/modal";
import RetailPriceTooltip from "../../../components/retailPriceTooltip";
import PriceLabel from "../../../components/price/priceSlab/priceLabel";
import questionMark from "../../../images/rrp_tooltip_mark.svg";
import { RRPSectionProps } from "./types";
import "./style.scss";

const RetailPriceInfo: FC<{}> = () => {
  const [showTooltip, setShowTooltip] = useState(false);
  const isRTL = useIsRTL();
  const message = useSelector(selectMessagesFromConfigs);

  const { isMobileView } = useBreakpoint();

  const handleCloseModal = () => setShowTooltip(false);

  return isMobileView ? (
    <>
      <img
        src={questionMark}
        alt="question-mark"
        className="question-mark"
        onClick={() => {
          setShowTooltip(true);
        }}
      />
      {showTooltip && (
        <ModalBfl
          showModal={showTooltip}
          modalClass={cn(
            "generic_modal",
            "retail-price-modal",
            "pdp_price_section",
            {
              arabic: isRTL,
              "responsive-view": true
            }
          )}
          closeModal={handleCloseModal}
        >
          <div className="tooltip-content">
            <div className="header-wrapper">
              <p className="header">{message?.pdpRrpPopupTitle}</p>

              <span className="cross_button" onClick={handleCloseModal}>
                &times;
              </span>
            </div>
            <p className="mb-2 main-text">{message?.pdpRrpPopupContent}</p>
          </div>
        </ModalBfl>
      )}
    </>
  ) : (
    <span className="retail-price-wrapper">
      <img
        src={questionMark}
        alt="question-mark"
        className="question-mark"
        onMouseEnter={() => {
          setShowTooltip(true);
        }}
        onMouseLeave={() => {
          setShowTooltip(false);
        }}
      />
      {showTooltip && <RetailPriceTooltip message={message} isArrow />}
    </span>
  );
};

const RRPSection = ({
  rrp,
  save,
  percentage,
  currencyCode
}: RRPSectionProps) => {
  const { t } = useTranslation("common");
  const isRTL = useIsRTL();
  const percentageValue = isRTL ? `%${percentage}` : `${percentage}%`;

  return (
    <div className="rrp_section">
      {rrp && (
        <span className="rrp_value rrp_value_inline">
          <span className="rrp_text">{`${t("price.rrp")}:`}</span>
          {currencyCode} {rrp}
        </span>
      )}
      <RetailPriceInfo />
      {percentage && (
        <span className="rrp_percentage">
          {`(${percentageValue} ${t("price.off")})`}
        </span>
      )}
    </div>
  );
};

const ProductPriceSection = () => {
  const item = useSelector(selectProductDetails);
  const language = useSelector(selectLanguageSlug);
  const { currentPrice, oldPrice } = useSelector(selectPDPPriceInfo);
  const currencyCode = useCurrency();
  const commonSettings = useSelector(selectCommonSettings);
  const isRTL = useIsRTL();
  const vatText = get(commonSettings, `${language}_vatText`, "");

  const isPriceSection = currentPrice || oldPrice;

  const rrpProps = useMemo(() => {
    if (!item.price?.rrp || item.price?.rrp?.price <= 0) return;
    return {
      ...getOptionalByValue({
        value: item.price.rrp.savedPriceLabel,
        keyName: "save"
      }),
      ...getOptionalByValue({
        value: item.price.rrp.discountPercentage,
        keyName: "percentage",
        callback: null
      }),
      ...getOptionalByValue({
        value: item.price.rrp.priceLabel,
        keyName: "rrp"
      })
    };
  }, [
    item.price?.rrp?.savedPrice,
    item.price?.rrp?.discountPercentage,
    item.price?.rrp?.price
  ]);

  const showRRP = !isEmpty(rrpProps);

  return (
    <div
      className={cn("product_price_wrapper", {
        arabic: isRTL
      })}
    >
      {Boolean(isPriceSection) && (
        <div
          id="price-slab"
          className={cn("item_price_slab price_slab", {
            arabic: isRTL
          })}
        >
          <PriceLabel product={item} />

          {showRRP && <RRPSection {...rrpProps} currencyCode={currencyCode} />}
        </div>
      )}
      {vatText && <span className="vat_price">{`(${vatText})`}</span>}
    </div>
  );
};

export default ProductPriceSection;
